/**
 * Pac-Man engine — pure game logic for the hidden arcade (C).
 * No DOM, no MobX. createGame() returns a state object; the component calls
 * step(state, input, dt) once per fixed tick and drains state.events.
 * Playfield is the classic 28×31 tile maze at 8 px per tile (224×248).
 */

export const TILE = 8;
export const COLS = 28;
export const ROWS = 31;
export const WIDTH = COLS * TILE;
export const HEIGHT = ROWS * TILE;
export const TICK_MS = 1000 / 60;
const EPS = 1e-6;

// # wall  . pellet  o power pellet  (space) corridor  - ghost door  x void
export const MAZE = [
    '############################',
    '#............##............#',
    '#.####.#####.##.#####.####.#',
    '#o####.#####.##.#####.####o#',
    '#.####.#####.##.#####.####.#',
    '#..........................#',
    '#.####.##.########.##.####.#',
    '#.####.##.########.##.####.#',
    '#......##....##....##......#',
    '######.##### ## #####.######',
    'xxxxx#.##### ## #####.#xxxxx',
    'xxxxx#.##          ##.#xxxxx',
    'xxxxx#.## ###--### ##.#xxxxx',
    '######.## #      # ##.######',
    '      .   #      #   .      ',
    '######.## #      # ##.######',
    'xxxxx#.## ######## ##.#xxxxx',
    'xxxxx#.##          ##.#xxxxx',
    'xxxxx#.## ######## ##.#xxxxx',
    '######.## ######## ##.######',
    '#............##............#',
    '#.####.#####.##.#####.####.#',
    '#.####.#####.##.#####.####.#',
    '#o..##.......  .......##..o#',
    '###.##.##.########.##.##.###',
    '###.##.##.########.##.##.###',
    '#......##....##....##......#',
    '#.##########.##.##########.#',
    '#.##########.##.##########.#',
    '#..........................#',
    '############################',
];

export const DIRS = {
    up: { dx: 0, dy: -1, name: 'up' },
    down: { dx: 0, dy: 1, name: 'down' },
    left: { dx: -1, dy: 0, name: 'left' },
    right: { dx: 1, dy: 0, name: 'right' },
};
export const OPPOSITE = {
    up: 'down', down: 'up', left: 'right', right: 'left',
};
// Classic tie-break order for ghost decisions.
const DIR_ORDER = ['up', 'left', 'down', 'right'];

export const PELLET_POINTS = 10;
export const POWER_POINTS = 50;
export const GHOST_POINTS = [200, 400, 800, 1600];
export const EXTRA_LIFE_AT = 10000;
export const FRIGHT_MS = 6000;
export const FRIGHT_MIN_MS = 1000;
export const FRIGHT_STEP_MS = 500;
export const FRIGHT_FLASH_MS = 2000;
export const READY_MS = 2000;
export const DYING_MS = 1500;
export const LEVEL_CLEARED_MS = 2000;
// scatter, chase, scatter, chase, scatter, chase, scatter — then chase forever
export const MODE_SCHEDULE_MS = [7000, 20000, 7000, 20000, 5000, 20000, 5000];

export const TUNNEL_ROW = 14;
export const HOUSE_EXIT = { x: 112, y: 92 };
export const HOUSE_CENTRE = { x: 112, y: 116 };
export const HOUSE_DOOR_TILE = { col: 13, row: 11 };
export const PACMAN_START = { x: 112, y: 188 };

export const SPEEDS = {
    pacman: 1.0, pacmanFright: 1.1, ghost: 0.95, ghostFright: 0.6, eyes: 2.0, house: 0.6,
};

export const GHOST_DEFS = [
    {
        name: 'blinky', start: { x: 112, y: 92 }, scatter: { col: 25, row: -3 }, releaseDots: 0, startsOutside: true,
    },
    {
        name: 'pinky', start: { x: 112, y: 116 }, scatter: { col: 2, row: -3 }, releaseDots: 0, startsOutside: false,
    },
    {
        name: 'inky', start: { x: 96, y: 116 }, scatter: { col: 27, row: 31 }, releaseDots: 30, startsOutside: false,
    },
    {
        name: 'clyde', start: { x: 128, y: 116 }, scatter: { col: 0, row: 31 }, releaseDots: 60, startsOutside: false,
    },
];

export function mazeChar(col, row) {
    if (row < 0 || row >= ROWS) {
        return 'x';
    }
    if (col < 0 || col >= COLS) {
        return row === TUNNEL_ROW ? ' ' : 'x';
    }
    return MAZE[row][col];
}

export function isWalkable(col, row, { door = false } = {}) {
    const c = mazeChar(col, row);
    if (c === '#' || c === 'x') {
        return false;
    }
    if (c === '-') {
        return door;
    }
    return true;
}

export function tileOf(px) {
    return Math.floor(px / TILE);
}

export function centreOf(tile) {
    return tile * TILE + TILE / 2;
}

export function levelSpeed(level) {
    return Math.min(1.25, 1 + 0.05 * (Math.max(1, level) - 1));
}

function buildDots() {
    const dots = new Uint8Array(COLS * ROWS);
    let count = 0;
    for (let row = 0; row < ROWS; row += 1) {
        for (let col = 0; col < COLS; col += 1) {
            const c = MAZE[row][col];
            if (c === '.') {
                dots[row * COLS + col] = 1;
                count += 1;
            } else if (c === 'o') {
                dots[row * COLS + col] = 2;
                count += 1;
            }
        }
    }
    return { dots, count };
}

function wrapTunnel(entity) {
    if (entity.x < -TILE / 2) {
        entity.x += WIDTH + TILE;
    } else if (entity.x > WIDTH + TILE / 2) {
        entity.x -= WIDTH + TILE;
    }
}

/**
 * Advances an entity `speed` px along entity.dir, stopping at every tile
 * centre to ask decide(entity, col, row) for the next direction (or null to
 * stop). Positions off the tile grid (x = 112 between two columns) simply
 * travel to the next centre first.
 */
function advance(entity, speed, decide) {
    let remaining = speed;
    let guard = 0;
    while (remaining > EPS && entity.moving && guard < 16) {
        guard += 1;
        const d = DIRS[entity.dir];
        const col = tileOf(entity.x);
        const row = tileOf(entity.y);
        const horizontal = d.dx !== 0;
        const pos = horizontal ? entity.x : entity.y;
        const centre = horizontal ? centreOf(col) : centreOf(row);
        const offset = pos - centre;
        if (Math.abs(offset) < EPS) {
            entity.x = centreOf(col);
            entity.y = centreOf(row);
            const next = decide(entity, col, row);
            if (!next) {
                entity.moving = false;
                break;
            }
            entity.dir = next;
            const nd = DIRS[next];
            const stepLen = Math.min(remaining, TILE);
            entity.x += nd.dx * stepLen;
            entity.y += nd.dy * stepLen;
            remaining -= stepLen;
        } else {
            const sign = d.dx + d.dy;
            let dist;
            if (sign > 0) {
                dist = offset < 0 ? -offset : TILE - offset;
            } else {
                dist = offset > 0 ? offset : TILE + offset;
            }
            const stepLen = Math.min(remaining, dist);
            entity.x += d.dx * stepLen;
            entity.y += d.dy * stepLen;
            remaining -= stepLen;
        }
        wrapTunnel(entity);
    }
}

function resetPositions(state) {
    state.pacman = {
        x: PACMAN_START.x,
        y: PACMAN_START.y,
        dir: 'left',
        nextDir: null,
        moving: true,
        mouth: 0,
    };
    state.ghosts = GHOST_DEFS.map(def => ({
        name: def.name,
        x: def.start.x,
        y: def.start.y,
        dir: def.startsOutside ? 'left' : 'up',
        mode: def.startsOutside ? state.globalMode : 'house',
        scatter: def.scatter,
        releaseDots: def.releaseDots,
        moving: true,
    }));
}

export function startLevel(state, level) {
    const built = buildDots();
    state.level = level;
    state.dots = built.dots;
    state.dotsLeft = built.count;
    state.dotsEaten = 0;
    state.frightMs = 0;
    state.frightFlash = false;
    state.frightChain = 0;
    state.modeIndex = 0;
    state.modeMs = MODE_SCHEDULE_MS[0];
    state.globalMode = 'scatter';
    resetPositions(state);
    state.status = 'ready';
    state.statusMs = READY_MS;
    state.startAnnounced = false;
    return state;
}

export function createGame({ rng = Math.random } = {}) {
    const state = {
        width: WIDTH,
        height: HEIGHT,
        rng,
        level: 1,
        score: 0,
        lives: 3,
        status: 'ready',
        statusMs: READY_MS,
        elapsedMs: 0,
        extraLifeGiven: false,
        globalMode: 'scatter',
        events: [],
    };
    return startLevel(state, 1);
}

export function ghostTarget(state, g) {
    if (g.mode === 'eyes') {
        return { col: HOUSE_DOOR_TILE.col, row: HOUSE_DOOR_TILE.row };
    }
    if (g.mode !== 'chase') {
        return { col: g.scatter.col, row: g.scatter.row };
    }
    const p = state.pacman;
    const pc = tileOf(p.x);
    const pr = tileOf(p.y);
    const pd = DIRS[p.dir] || DIRS.left;
    if (g.name === 'blinky') {
        return { col: pc, row: pr };
    }
    if (g.name === 'pinky') {
        return { col: pc + 4 * pd.dx, row: pr + 4 * pd.dy };
    }
    if (g.name === 'inky') {
        const blinky = state.ghosts.find(other => other.name === 'blinky') || g;
        const aheadCol = pc + 2 * pd.dx;
        const aheadRow = pr + 2 * pd.dy;
        return { col: 2 * aheadCol - tileOf(blinky.x), row: 2 * aheadRow - tileOf(blinky.y) };
    }
    // clyde: chase when far, retreat to the corner when within 8 tiles
    const dc = pc - tileOf(g.x);
    const dr = pr - tileOf(g.y);
    if (dc * dc + dr * dr > 64) {
        return { col: pc, row: pr };
    }
    return { col: g.scatter.col, row: g.scatter.row };
}

function ghostDecide(state, g, col, row) {
    const options = DIR_ORDER.filter(name => {
        if (name === OPPOSITE[g.dir]) {
            return false;
        }
        const d = DIRS[name];
        return isWalkable(col + d.dx, row + d.dy);
    });
    if (options.length === 0) {
        const back = OPPOSITE[g.dir];
        const d = DIRS[back];
        return isWalkable(col + d.dx, row + d.dy) ? back : null;
    }
    if (g.mode === 'frightened') {
        return options[Math.min(options.length - 1, Math.floor(state.rng() * options.length))];
    }
    const target = ghostTarget(state, g);
    let best = options[0];
    let bestDist = Infinity;
    options.forEach(name => {
        const d = DIRS[name];
        const nc = col + d.dx;
        const nr = row + d.dy;
        const dist = (nc - target.col) ** 2 + (nr - target.row) ** 2;
        if (dist < bestDist) {
            bestDist = dist;
            best = name;
        }
    });
    return best;
}

/** Straight-line slide used inside/through the ghost house. True when arrived. */
function slideTo(g, speed, target) {
    const dx = target.x - g.x;
    if (Math.abs(dx) > EPS) {
        const stepLen = Math.min(speed, Math.abs(dx));
        g.x += Math.sign(dx) * stepLen;
        g.dir = dx > 0 ? 'right' : 'left';
        return false;
    }
    const dy = target.y - g.y;
    if (Math.abs(dy) > EPS) {
        const stepLen = Math.min(speed, Math.abs(dy));
        g.y += Math.sign(dy) * stepLen;
        g.dir = dy > 0 ? 'down' : 'up';
        return false;
    }
    return true;
}

function releaseGhosts(state) {
    state.ghosts.forEach(g => {
        if (g.mode === 'house' && state.dotsEaten >= g.releaseDots) {
            g.mode = 'leaving';
        }
    });
}

function moveGhost(state, g) {
    const lvl = levelSpeed(state.level);
    if (g.mode === 'house') {
        return;
    }
    if (g.mode === 'leaving') {
        if (slideTo(g, SPEEDS.house * lvl, HOUSE_EXIT)) {
            g.x = HOUSE_EXIT.x;
            g.y = HOUSE_EXIT.y;
            g.dir = 'left';
            g.moving = true;
            g.mode = state.frightMs > 0 ? 'frightened' : state.globalMode;
        }
        return;
    }
    if (g.mode === 'entering') {
        if (slideTo(g, SPEEDS.eyes, HOUSE_CENTRE)) {
            g.mode = 'leaving';
        }
        return;
    }
    if (g.mode === 'eyes') {
        const onDoorRow = Math.abs(g.y - HOUSE_EXIT.y) < EPS;
        const atDoorX = Math.abs(g.x - centreOf(13)) < EPS || Math.abs(g.x - centreOf(14)) < EPS;
        if (onDoorRow && atDoorX) {
            g.mode = 'entering';
            return;
        }
    }
    let speed;
    if (g.mode === 'eyes') {
        speed = SPEEDS.eyes;
    } else if (g.mode === 'frightened') {
        speed = SPEEDS.ghostFright * lvl;
    } else {
        speed = SPEEDS.ghost * lvl;
    }
    g.moving = true;
    advance(g, speed, (entity, col, row) => {
        // Eyes decide at every tile centre they cross, so a ghost eaten
        // mid-tile (fractional offset) still notices the door tile instead of
        // overshooting it within the same tick and orbiting the house forever.
        if (entity.mode === 'eyes' && row === HOUSE_DOOR_TILE.row
            && (col === HOUSE_DOOR_TILE.col || col === HOUSE_DOOR_TILE.col + 1)) {
            entity.mode = 'entering';
            return null;
        }
        return ghostDecide(state, entity, col, row);
    });
}

function movePacman(state, input) {
    const p = state.pacman;
    if (input && input.dir && DIRS[input.dir]) {
        p.nextDir = input.dir;
    }
    if (p.nextDir && p.nextDir === OPPOSITE[p.dir]) {
        p.dir = p.nextDir;
        p.nextDir = null;
        p.moving = true;
    } else if (!p.moving && p.nextDir) {
        const d = DIRS[p.nextDir];
        if (isWalkable(tileOf(p.x) + d.dx, tileOf(p.y) + d.dy)) {
            p.dir = p.nextDir;
            p.nextDir = null;
            p.moving = true;
        }
    }
    if (!p.moving) {
        return;
    }
    const speed = (state.frightMs > 0 ? SPEEDS.pacmanFright : SPEEDS.pacman) * levelSpeed(state.level);
    advance(p, speed, (entity, col, row) => {
        if (entity.nextDir) {
            const nd = DIRS[entity.nextDir];
            if (isWalkable(col + nd.dx, row + nd.dy)) {
                const chosen = entity.nextDir;
                entity.nextDir = null;
                return chosen;
            }
        }
        const d = DIRS[entity.dir];
        return isWalkable(col + d.dx, row + d.dy) ? entity.dir : null;
    });
    p.mouth += 0.2;
}

function addScore(state, points) {
    state.score += points;
    if (!state.extraLifeGiven && state.score >= EXTRA_LIFE_AT) {
        state.extraLifeGiven = true;
        state.lives += 1;
        state.events.push({ type: 'extraLife' });
    }
}

function eatDots(state) {
    const col = tileOf(state.pacman.x);
    const row = tileOf(state.pacman.y);
    if (col < 0 || col >= COLS || row < 0 || row >= ROWS) {
        return;
    }
    const idx = row * COLS + col;
    const dot = state.dots[idx];
    if (!dot) {
        return;
    }
    state.dots[idx] = 0;
    state.dotsLeft -= 1;
    state.dotsEaten += 1;
    if (dot === 1) {
        addScore(state, PELLET_POINTS);
        state.events.push({ type: 'pellet' });
        return;
    }
    addScore(state, POWER_POINTS);
    state.events.push({ type: 'power' });
    state.frightMs = Math.max(FRIGHT_MIN_MS, FRIGHT_MS - FRIGHT_STEP_MS * (state.level - 1));
    state.frightFlash = false;
    state.frightChain = 0;
    state.ghosts.forEach(g => {
        if (g.mode === 'scatter' || g.mode === 'chase') {
            g.mode = 'frightened';
            g.dir = OPPOSITE[g.dir];
        }
    });
    state.events.push({ type: 'frightStart' });
}

function updateModes(state, dtMs) {
    if (state.frightMs > 0) {
        state.frightMs = Math.max(0, state.frightMs - dtMs);
        state.frightFlash = state.frightMs > 0 && state.frightMs <= FRIGHT_FLASH_MS;
        if (state.frightMs === 0) {
            state.frightFlash = false;
            state.ghosts.forEach(g => {
                if (g.mode === 'frightened') {
                    g.mode = state.globalMode;
                }
            });
            state.events.push({ type: 'frightEnd' });
        }
        return;
    }
    if (state.modeIndex >= MODE_SCHEDULE_MS.length) {
        return;
    }
    state.modeMs -= dtMs;
    if (state.modeMs > 0) {
        return;
    }
    state.modeIndex += 1;
    state.globalMode = state.modeIndex % 2 === 0 ? 'scatter' : 'chase';
    state.modeMs = state.modeIndex < MODE_SCHEDULE_MS.length ? MODE_SCHEDULE_MS[state.modeIndex] : Infinity;
    state.ghosts.forEach(g => {
        if (g.mode === 'scatter' || g.mode === 'chase') {
            g.mode = state.globalMode;
            g.dir = OPPOSITE[g.dir];
        }
    });
}

function checkCollisions(state) {
    const pc = tileOf(state.pacman.x);
    const pr = tileOf(state.pacman.y);
    for (let i = 0; i < state.ghosts.length; i += 1) {
        const g = state.ghosts[i];
        if (tileOf(g.x) === pc && tileOf(g.y) === pr) {
            if (g.mode === 'frightened') {
                const chain = Math.min(state.frightChain, GHOST_POINTS.length - 1);
                addScore(state, GHOST_POINTS[chain]);
                state.frightChain += 1;
                g.mode = 'eyes';
                state.events.push({ type: 'ghostEaten', points: GHOST_POINTS[chain] });
            } else if (g.mode === 'scatter' || g.mode === 'chase') {
                state.status = 'dying';
                state.statusMs = DYING_MS;
                state.frightMs = 0;
                state.frightFlash = false;
                state.events.push({ type: 'death' });
                return;
            }
        }
    }
}

function countdown(state, dtMs) {
    state.statusMs -= dtMs;
    return state.statusMs <= 0;
}

export function step(state, input = {}, dtMs = TICK_MS) {
    if (state.status === 'gameOver') {
        return state;
    }
    state.elapsedMs += dtMs;

    if (state.status === 'ready') {
        if (!state.startAnnounced) {
            state.startAnnounced = true;
            state.events.push({ type: 'start' });
        }
        if (input && input.dir && DIRS[input.dir]) {
            state.pacman.nextDir = input.dir;
        }
        if (countdown(state, dtMs)) {
            state.status = 'playing';
        }
        return state;
    }
    if (state.status === 'dying') {
        if (countdown(state, dtMs)) {
            state.lives -= 1;
            if (state.lives <= 0) {
                state.lives = 0;
                state.status = 'gameOver';
                state.events.push({ type: 'gameOver' });
            } else {
                resetPositions(state);
                state.status = 'ready';
                state.statusMs = READY_MS;
                state.startAnnounced = false;
            }
        }
        return state;
    }
    if (state.status === 'levelCleared') {
        if (countdown(state, dtMs)) {
            startLevel(state, state.level + 1);
        }
        return state;
    }

    updateModes(state, dtMs);
    movePacman(state, input);
    eatDots(state);
    releaseGhosts(state);
    state.ghosts.forEach(g => moveGhost(state, g));
    checkCollisions(state);
    if (state.status !== 'playing') {
        return state;
    }
    if (state.dotsLeft === 0) {
        state.status = 'levelCleared';
        state.statusMs = LEVEL_CLEARED_MS;
        state.events.push({ type: 'levelCleared' });
    }
    return state;
}
